import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MoonbucksLogo from "../components/MoonbucksLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckCircle2, MailCheck } from "lucide-react";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Backend intentionally returns 200 for unknown emails to prevent
      // enumeration — this catch only fires on real network / server errors.
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Forgot password?" sub="Type your email — we'll send you a reset link.">
      {sent ? (
        <div className="text-center py-6 space-y-3" data-testid="forgot-password-sent">
          <MailCheck className="w-12 h-12 text-mb-emerald mx-auto" />
          <h3 className="font-display font-semibold text-lg">Check your inbox</h3>
          <p className="text-sm text-neutral-600 leading-relaxed">
            If <span className="font-mono-data">{email}</span> is registered, you&rsquo;ll get an email with a reset link within a minute. The link expires in 30 minutes.
          </p>
          <p className="text-xs text-neutral-400 pt-2">
            Nothing showing up? Check spam — or try a different email.
          </p>
          <Link to="/login" className="text-sm text-mb-emerald font-semibold inline-block pt-2">Back to login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
          <Field label="Email">
            <Input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-password-email-input"
            />
          </Field>
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md h-11"
            data-testid="forgot-password-submit-btn"
          >
            {busy ? "Sending…" : "Send reset link"}
          </Button>
          <p className="text-xs text-center text-neutral-500">
            Remembered it? <Link to="/login" className="text-mb-emerald font-semibold">Back to login</Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { setSession } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords don't match.");
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (!token) return toast.error("Missing or invalid reset link.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      // Auto-login the user with the returned token
      if (setSession) setSession(data.access_token, data.user);
      setDone(true);
      setTimeout(() => nav(data.user?.role === "admin" ? "/admin" : "/app"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" sub="This reset link is missing its token.">
        <div className="text-center space-y-3 py-4">
          <p className="text-sm text-neutral-600">Please request a fresh reset link.</p>
          <Link to="/forgot-password" className="text-mb-emerald font-semibold">Request new link</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" sub="At least 8 characters. Something you'll remember this time.">
      {done ? (
        <div className="text-center py-6 space-y-3" data-testid="reset-password-done">
          <CheckCircle2 className="w-12 h-12 text-mb-emerald mx-auto" />
          <h3 className="font-display font-semibold text-lg">Password updated</h3>
          <p className="text-sm text-neutral-600">Signing you in&hellip;</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <Field label="New password">
            <Input
              type="password"
              required
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="reset-password-new-input"
            />
          </Field>
          <Field label="Confirm new password">
            <Input
              type="password"
              required
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              data-testid="reset-password-confirm-input"
            />
          </Field>
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md h-11"
            data-testid="reset-password-submit-btn"
          >
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

// Local copies of the presentation helpers so we don't refactor Auth.jsx here.
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-neutral-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function AuthShell({ title, sub, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-mb-cream px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded-full bg-mb-emerald flex items-center justify-center text-white">
            <MoonbucksLogo className="w-5 h-5" />
          </div>
          <span className="font-serif-hero text-2xl leading-none">Moonbucks</span>
        </Link>
        <div className="bg-white rounded-2xl border border-mb-border p-8 shadow-sm">
          <h1 className="font-display text-2xl font-semibold mb-1">{title}</h1>
          <p className="text-sm text-neutral-600 mb-6">{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
